/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getAssetByDeviceId} from '@ion/core/src/services/Connect';
import {Asset} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseGetAssetByDeviceIdResult = UseDefaultQueryResult & {
  result: Asset[];
};

export interface UseGetAssetByDeviceIdParams {
  deviceId: number;
};

/**
 * Retrieves the asset data for the given Device ID.
 *
 * ```typescript
 *
 *  import {useGetAssetByDeviceId} from '@ion/query/src/hooks/useGetAssetByDeviceId';
 *
 *  const {result: asset} = useGetAssetByDeviceId({deviceId});
 *
 * ```
 */
export const useGetAssetByDeviceId = ({
  deviceId
}: UseGetAssetByDeviceIdParams,
enabled = true,
staleTime = getStaleTime(1, false, false)
): UseGetAssetByDeviceIdResult => {
  const queryClient = useQueryClient();
  const key         = useMemo(() => (
    createKey(QueryKey.ASSET_BY_DEVICE_ID, deviceId)
  ), [deviceId]);

  const query = useQuery(key, async() => {
    if (deviceId) {
      try {
        const result = await getAssetByDeviceId(deviceId);
        return result;
      } catch (err) {
        error(err);
        return [];
      }
    }
    return [];
  }, {
    staleTime : staleTime as number,
    enabled
  });

  return createQueryResult(query, {
    result : query.data || []
  }) as UseGetAssetByDeviceIdResult;
};

export default useGetAssetByDeviceId;
